import {html, nothing, type HTMLTemplateResult} from "lit";
import {keyed} from "lit/directives/keyed.js";
import {pad2} from "../../../../domain/gimmicks/stopwatch.state";
import type {SkinInput} from "./skin-input";

/**
 * One split-flap leaf. Keyed by its position AND its value: when the digit changes, Lit renders a fresh
 * `.stopwatch-flap__fold` whose CSS animation (`rotateX`, ralphy.css) folds the upper half down once —
 * a still digit keeps its node and does not animate again.
 */
function leaf(position: string, char: string): HTMLTemplateResult {
    return html`
        <span class="stopwatch-flap__leaf">
            <span class="stopwatch-flap__top">${char}</span>
            <span class="stopwatch-flap__bottom">${char}</span>
            ${keyed(`${position}-${char}`, html`<span class="stopwatch-flap__fold">${char}</span>`)}
        </span>
    `;
}

function group(name: string, value: string): HTMLTemplateResult {
    return html`<span class="stopwatch-flap__group">${[...value].map((char, i) => leaf(`${name}${i}`, char))}</span>`;
}

/**
 * "Fallblatt": minutes and seconds (from one hour on also hours) on split-flap leaves that fold down on
 * every change, like a station board; the hundredths small beside them, not folding. From one hour on the
 * board gets the modifier `--long`: more leaves, smaller type (ralphy.css).
 */
export function renderFlapSkin({parts}: SkinInput): HTMLTemplateResult {
    return html`
        <div class=${parts.h > 0 ? 'stopwatch-flap stopwatch-flap--long' : 'stopwatch-flap'} aria-hidden="true">
            ${parts.h > 0 ? html`${group('h', String(parts.h))}<span class="stopwatch-flap__colon">:</span>` : nothing}
            ${group('m', pad2(parts.m))}
            <span class="stopwatch-flap__colon">:</span>
            ${group('s', pad2(parts.s))}
            <span class="stopwatch-flap__hundredths stopwatch__hundredths">${pad2(parts.cs)}</span>
        </div>
    `;
}
